import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useToggle } from 'Shared/utils/hooks';

import { QuickStartGuideItemIDs } from 'Dashboard/components/QuickStartGuide/QuickStartGuideItems.constants';
import useUpdateQuickStartGuideFlag from 'Dashboard/components/QuickStartGuide/useUpdateQuickStartGuideFlag';
import { validEmail, defaultPersonalMessage, isEmptyString } from 'Shared/helpers';
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';
import InviteCollaboratorsInput from 'Dashboard/components/InviteCollaboratorsInput/InviteCollaboratorsInput';
import SimpleAddWorkspaceWrapper from 'Shared/components/modals/SimpleAddWorkspaceModals/SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';
import AddGroupSugestionsSection from 'Shared/components/modals/SimpleAddWorkspaceModals/SuggestionsSection/AddGroupSugestionsSection/AddGroupSugestionsSection';
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import GroupWorkspaceNameInput from './GroupWorkspaceNameInput/GroupWorkspaceNameInput';
import TrackingConstants from 'Shared/constants/Tracking.constants';
import { Illustrations } from 'Wpds/components/illustration';
import { WorkspaceType } from 'Shared/constants/types';

type Props = {
  withRedirect?: boolean;
  onSuccess?: Function;
  onClose: Function;
  defaultRecurringMeetingId?: number;
  defaultInviteeEmails?: {
    value: string;
    label: string;
    fromCalendar: boolean;
  }[];
  defaultWorkspaceName?: string;
  includeSuggestions?: boolean;
  calendarMeetingId?: string;
  inviteMethod?: string;
};

function AddGroupModalWithCalendarIntegration({
  withRedirect = true,
  defaultRecurringMeetingId = null,
  defaultInviteeEmails: defaultInviteeEmails = [],
  defaultWorkspaceName = '',
  onSuccess,
  onClose,
  includeSuggestions = true,
  calendarMeetingId,
  inviteMethod = TrackingConstants.INVITE_METHOD_TYPES.CREATE_GROUP_WORKSPACE_MODAL,
}: Props) {
  const { tr } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const { handleCompleteItem } = useUpdateQuickStartGuideFlag();
  const [isSuggestionOpened, toggleIsSuggestionOpened] = useToggle(false);

  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [inviteeEmails, setInviteeEmails] = useState(defaultInviteeEmails);
  const [workspaceName, setWorkspaceName] = useState(defaultWorkspaceName);
  const [recurringMeetingId, setRecurringMeetingId] = useState(defaultRecurringMeetingId);

  const handleWorkspaceNameChange = useCallback(event => setWorkspaceName(event.target.value), []);

  const handleInviteesChange = useCallback(value => {
    window?.analytics?.track?.('Clicked in Add Workspace Modal', {
      modal: 'add_group',
      button: 'Meeting',
    });
    setInviteeEmails(value);
  }, []);

  const isAllEmailsValid = useCallback(
    () => (inviteeEmails || []).every(({ value }) => validEmail(value)),
    [inviteeEmails]
  );

  const areAllEmailWithinAllowedPiDomains = useCallback(
    () => (inviteeEmails || []).every(({ value }) => isEmployeeEmailOrValidDomain({ email: value })),
    [inviteeEmails]
  );

  const disabledSubmit =
    isSubmitting || isEmptyString(workspaceName) || !isAllEmailsValid() || !areAllEmailWithinAllowedPiDomains();

  const handleSubmit = async () => {
    toggleIsSubmitting(true);
    const firstCreatedWorkspace = await dispatch(
      bulkCreateWorkspaces({
        inviteMethod,
        workspacesParams: [
          {
            name: workspaceName,
            isGroup: true,
            inviteeEmails: inviteeEmails.map(({ value }) => value),
            recurringMeetingId,
            calendarMeetingId: recurringMeetingId ? null : calendarMeetingId,
            personalMessage: defaultPersonalMessage(workspaceName),
            inviteeEmailsFromCalendar: inviteeEmails.filter(email => email.fromCalendar).map(({ value }) => value),
          },
        ],
      })
    );
    toggleIsSubmitting(false);
    handleCompleteItem(QuickStartGuideItemIDs.setupGroupWorkspace);
    if (!firstCreatedWorkspace?.path) return;
    if (onSuccess) onSuccess(firstCreatedWorkspace);
    if (withRedirect) {
      history.push(firstCreatedWorkspace?.path);
    } else {
      onClose();
    }
  };

  return (
    <SimpleAddWorkspaceWrapper
      onClose={onClose}
      workspaceType={WorkspaceType.isGroup}
      title={tr('perform.workspace.simple_add_group.title')}
      description=""
      disabledSubmit={disabledSubmit}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      createNameSection={
        <GroupWorkspaceNameInput
          isSuggestionOpened={isSuggestionOpened}
          recurringMeetingId={recurringMeetingId}
          workspaceName={workspaceName}
          onWorkspaceNameSet={setWorkspaceName}
          onWorkspaceNameChange={handleWorkspaceNameChange}
          onInviteesChange={handleInviteesChange}
          onSetRecurringMeetingId={setRecurringMeetingId}
          onSetIsSuggestionOpened={toggleIsSuggestionOpened}
        />
      }
      optionToInviteSection={
        <InviteCollaboratorsInput
          value={inviteeEmails}
          label=""
          onChange={handleInviteesChange}
          placeholder={tr('perform.workspace.simple_add_group.invite_placeholder')}
        />
      }
      suggestionsSection={
        includeSuggestions && (
          <AddGroupSugestionsSection
            onWorkspaceNameSet={setWorkspaceName}
            onInviteesChange={handleInviteesChange}
            onSetRecurringMeetingId={setRecurringMeetingId}
            onSetIsSuggestionOpened={toggleIsSuggestionOpened}
          />
        )
      }
      illustration={Illustrations.two_people_at_table_in_conference_call_purple_bg}
      createNameSectionTitle={tr('perform.workspace.create_name_section.title')}
    />
  );
}

export default AddGroupModalWithCalendarIntegration;
